import { getRemoteConfig, fetchAndActivate, getValue } from 'firebase/remote-config';
import app from '../services/firebase';

// API Configuration defaults
export const API_CONFIG = {
  development: {
    baseUrl: 'http://localhost:5001/sharegiver-prod/us-central1/api', // Local API for development
  },
  production: {
    // api.sharegiver.com DNS is NXDOMAIN; use Firebase Hosting API site until custom domain is wired.
    baseUrl: 'https://sharegiver-api.web.app',
  },
};

let cachedBaseUrl: string | null = null;

export const getApiBaseUrl = async (): Promise<string> => {
  // Force use local API in development (ignore cache temporarily)
  if (process.env.NODE_ENV === 'development') {
    const devUrl = 'http://localhost:5001/sharegiver-prod/us-central1/api';
    // #region agent log
    fetch('http://127.0.0.1:7550/ingest/04d8ad19-bf18-48f6-bd8a-8ab8dfd2699d',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'c313e2'},body:JSON.stringify({sessionId:'c313e2',runId:'pre-fix',hypothesisId:'C',location:'admin-web/src/config/api.ts:dev',message:'admin getApiBaseUrl forced localhost',data:{devUrl,nodeEnv:process.env.NODE_ENV,envApi:process.env.REACT_APP_API_BASE_URL||null},timestamp:Date.now()})}).catch(()=>{});
    // #endregion
    return devUrl;
  }

  if (cachedBaseUrl) {
    // #region agent log
    fetch('http://127.0.0.1:7550/ingest/04d8ad19-bf18-48f6-bd8a-8ab8dfd2699d',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'c313e2'},body:JSON.stringify({sessionId:'c313e2',runId:'pre-fix',hypothesisId:'B',location:'admin-web/src/config/api.ts:cache',message:'admin getApiBaseUrl cache hit',data:{cachedBaseUrl,nodeEnv:process.env.NODE_ENV},timestamp:Date.now()})}).catch(()=>{});
    // #endregion
    return cachedBaseUrl;
  }

  // Check for environment variable first (highest priority)
  if (process.env.REACT_APP_API_BASE_URL) {
    cachedBaseUrl = process.env.REACT_APP_API_BASE_URL;
    // #region agent log
    fetch('http://127.0.0.1:7550/ingest/04d8ad19-bf18-48f6-bd8a-8ab8dfd2699d',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'c313e2'},body:JSON.stringify({sessionId:'c313e2',runId:'pre-fix',hypothesisId:'C',location:'admin-web/src/config/api.ts:env',message:'admin getApiBaseUrl from env',data:{cachedBaseUrl,nodeEnv:process.env.NODE_ENV},timestamp:Date.now()})}).catch(()=>{});
    // #endregion
    return cachedBaseUrl;
  }

  try {
    const rc = getRemoteConfig(app);
    rc.settings = {
      minimumFetchIntervalMillis: process.env.NODE_ENV === 'development' ? 0 : 60 * 60 * 1000,
      fetchTimeoutMillis: 5_000,
    } as typeof rc.settings;

    rc.defaultConfig = {
      api_base_url: API_CONFIG[process.env.NODE_ENV === 'production' ? 'production' : 'development'].baseUrl,
      app_env: process.env.NODE_ENV || 'development',
    } as Record<string, string>;

    await fetchAndActivate(rc);
    const apiBaseUrl = getValue(rc, 'api_base_url').asString();
    cachedBaseUrl = apiBaseUrl || API_CONFIG.development.baseUrl;
    // #region agent log
    fetch('http://127.0.0.1:7550/ingest/04d8ad19-bf18-48f6-bd8a-8ab8dfd2699d',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'c313e2'},body:JSON.stringify({sessionId:'c313e2',runId:'pre-fix',hypothesisId:'B',location:'admin-web/src/config/api.ts:remote-config',message:'admin getApiBaseUrl from Remote Config',data:{apiBaseUrl,cachedBaseUrl,nodeEnv:process.env.NODE_ENV},timestamp:Date.now()})}).catch(()=>{});
    // #endregion
    return cachedBaseUrl;
  } catch (error) {
    // Fallback to defaults on failure
    const environment = process.env.NODE_ENV === 'production' ? 'production' : 'development';
    cachedBaseUrl = API_CONFIG[environment as keyof typeof API_CONFIG].baseUrl;
    // #region agent log
    fetch('http://127.0.0.1:7550/ingest/04d8ad19-bf18-48f6-bd8a-8ab8dfd2699d',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'c313e2'},body:JSON.stringify({sessionId:'c313e2',runId:'pre-fix',hypothesisId:'E',location:'admin-web/src/config/api.ts:fallback',message:'admin getApiBaseUrl fallback',data:{cachedBaseUrl,environment,errorMessage:error instanceof Error ? error.message : String(error)},timestamp:Date.now()})}).catch(()=>{});
    // #endregion
    return cachedBaseUrl;
  }
};

// API Client class
class ApiClient {
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<{ data: T }> {
    const baseUrl = await getApiBaseUrl();
    const url = `${baseUrl}${endpoint}`;
    
    const token = localStorage.getItem('authToken');
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return { data };
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error('An unexpected error occurred');
    }
  }

  async get<T>(endpoint: string): Promise<{ data: T }> {
    return this.makeRequest<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body?: any): Promise<{ data: T }> {
    return this.makeRequest<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: any): Promise<{ data: T }> {
    return this.makeRequest<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<{ data: T }> {
    return this.makeRequest<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<{ data: T }> {
    return this.makeRequest<T>(endpoint, { method: 'DELETE' });
  }
}

export const apiClient = new ApiClient();
